import type { MedplumClient } from '@medplum/core';
import type { Organization, Patient, Practitioner, RelatedPerson } from '@medplum/fhirtypes';

import { EXTENSION_URLS } from '../features/rbac/constants';
import { getPatientOrganizationRef } from '@unify/fhir-model/resources/patient';
import { getPractitionerOrganizationRef } from '@unify/fhir-model/resources/practitioner';

export type PracticeOrganizationProfile = Practitioner | Patient | RelatedPerson | null | undefined;

export interface ResolvePracticeOrganizationOptions {
  /** Active portal patient id (guarantor / related-person switcher). */
  portalPatientId?: string;
}

function organizationIdFromReference(reference: string | undefined): string | undefined {
  if (!reference?.startsWith('Organization/')) return undefined;
  return reference.replace(/^Organization\//, '');
}

function patientIdFromReference(reference: string | undefined): string | undefined {
  if (!reference?.startsWith('Patient/')) return undefined;
  return reference.replace(/^Patient\//, '');
}

function portalSubjectPatientId(patient: Patient): string | undefined {
  const ref = patient.extension?.find((e) => e.url === EXTENSION_URLS.portalSubjectPatient)
    ?.valueReference?.reference;
  return patientIdFromReference(ref);
}

async function readOrganizationById(
  medplum: MedplumClient,
  orgId: string,
): Promise<Organization | null> {
  try {
    return await medplum.readResource('Organization', orgId);
  } catch {
    return null;
  }
}

async function resolveOrganizationFromPatientId(
  medplum: MedplumClient,
  patientId: string,
  cachedPatient?: Patient,
): Promise<Organization | null> {
  try {
    const patient =
      cachedPatient?.id === patientId
        ? cachedPatient
        : await medplum.readResource('Patient', patientId);
    // Check both the standard FHIR field and the Unify custom extension — seeded
    // patients use the extension rather than managingOrganization.
    const orgRef =
      patient.managingOrganization?.reference ??
      getPatientOrganizationRef(patient)?.reference;
    const orgId = organizationIdFromReference(orgRef);
    if (!orgId) return null;
    return readOrganizationById(medplum, orgId);
  } catch {
    return null;
  }
}

function resolvePortalPatientId(
  profile: PracticeOrganizationProfile,
  portalPatientId?: string,
): string | undefined {
  if (portalPatientId) return portalPatientId;

  if (profile?.resourceType === 'Patient' && profile.id) {
    return portalSubjectPatientId(profile) ?? profile.id;
  }

  if (profile?.resourceType === 'RelatedPerson') {
    return patientIdFromReference(profile.patient?.reference);
  }

  return undefined;
}

/**
 * Resolve the Organization that owns practice-level settings (template format,
 * branding logo, etc.).
 *
 * - Practitioners: linked practice on the profile.
 * - Patients / portal users: `Patient.managingOrganization` or the Unify org extension for the active chart.
 * - Staff without a link: first visible Organization (search fallback).
 */
export async function resolvePracticeOrganization(
  medplum: MedplumClient,
  profile?: PracticeOrganizationProfile,
  options: ResolvePracticeOrganizationOptions = {},
): Promise<Organization | null> {
  if (profile?.resourceType === 'Practitioner') {
    const linkedOrgId = organizationIdFromReference(
      getPractitionerOrganizationRef(profile)?.reference,
    );
    if (linkedOrgId) {
      const org = await readOrganizationById(medplum, linkedOrgId);
      if (org) return org;
    }
  } else {
    const patientId = resolvePortalPatientId(profile, options.portalPatientId);
    if (patientId) {
      const cachedPatient = profile?.resourceType === 'Patient' ? profile : undefined;
      const org = await resolveOrganizationFromPatientId(medplum, patientId, cachedPatient);
      if (org) return org;
    }
  }

  try {
    const bundle = await medplum.search('Organization', { _count: '1' });
    const org = (bundle.entry ?? [])
      .map((e) => e.resource as Organization | undefined)
      .find((o): o is Organization => Boolean(o));
    return org ?? null;
  } catch {
    return null;
  }
}

export function getPracticeOrganizationId(organization: Organization | null | undefined): string | null {
  return organization?.id ?? null;
}
